import currency from 'currency.js'
import { StripeInterval } from '@/reducers/subscription.slice'
import { StripeProduct } from '@/actions/settings/billingActions'
import { planCardTitle } from '@/features/user/settings/sections/plan-change/lib/helpers'
import {
  tierDisplayNamesFor,
  type PlanSegment,
} from '@/features/user/settings/sections/plan-change/lib/tierDisplayNames'
import { PLAN_CHANGE_TIERS } from '@/features/user/settings/sections/plan-change/lib/planChange.types'
import { FREE_TRIAL_2026 } from '../helpers'

export const getProductByInterval = (
  products: StripeProduct[],
  interval: StripeInterval
) =>
  products.find(
    (product) => product.default_price.recurring.interval === interval
  )

/** Core tiers take the name the plan chooser shows, so payment cannot disagree with it through Stripe metadata. */
export const signupPlanName = (
  product: Pick<StripeProduct, 'name' | 'metadata'> | undefined,
  viewer: PlanSegment
): string | undefined => {
  const stripeName = product?.metadata?.displayName || product?.name
  const productTier: string | undefined = product?.metadata?.tier
  const tier = PLAN_CHANGE_TIERS.find((candidate) => candidate === productTier)
  return tier
    ? planCardTitle(tier, stripeName, tierDisplayNamesFor(viewer))
    : stripeName
}

export const discountedCatchUpBkAmount = (
  amount: currency.Any,
  percentOff: number | null | undefined
): currency =>
  currency(amount).subtract(currency(amount).multiply((percentOff ?? 0) / 100))

/** Trial codes whose catch-up bookkeeping is waived outright, when the waiver flag is on. */
const CUBK_WAIVED_TRIAL_CODES: readonly string[] = [FREE_TRIAL_2026]

export enum CatchUpBkTreatment {
  chargeAtSignup = 'charge-at-signup',
  deferToPostTrial = 'defer-to-post-trial',
  waived = 'waived',
}

/**
 * Whether the fee is waived outright. Deliberately independent of the two-checkout
 * flow: that flow exists only to collect this fee, so it is decided *from* this
 * answer rather than feeding into it.
 */
export const isCatchUpBkWaived = ({
  trialCode,
  freeTrialEnabled,
  waiverEnabled,
}: {
  trialCode: string | null | undefined
  freeTrialEnabled: boolean
  waiverEnabled: boolean
}): boolean =>
  Boolean(
    freeTrialEnabled &&
    waiverEnabled &&
    trialCode &&
    CUBK_WAIVED_TRIAL_CODES.includes(trialCode)
  )

/**
 * Whether this signup really runs the two-checkout flow. The CRM answers "does this
 * user have multiple discounts"; a waived fee overrides it, because that second
 * checkout exists only to collect the fee.
 */
export const resolveTwoCheckoutFlow = ({
  needsTwoCheckouts,
  trialCode,
  freeTrialEnabled,
  waiverEnabled,
}: {
  needsTwoCheckouts: boolean
  trialCode: string | null | undefined
  freeTrialEnabled: boolean
  waiverEnabled: boolean
}): boolean =>
  needsTwoCheckouts &&
  !isCatchUpBkWaived({ trialCode, freeTrialEnabled, waiverEnabled })

/**
 * The trial summary under the plan total. Reads the treatment so the sentence cannot
 * promise a fee the signup will not charge — a waived cohort has no discount for the
 * amount alone to zero out, so the amount cannot be trusted to decide this.
 */
export const planSubText = ({
  treatment,
  freeTrialDays,
  productPriceLabel,
  isAnnual,
  catchUpBkLabel,
}: {
  treatment: CatchUpBkTreatment
  freeTrialDays: number | undefined
  productPriceLabel: string
  isAnnual: boolean
  catchUpBkLabel: string | null
}): string => {
  const tail =
    catchUpBkLabel && treatment !== CatchUpBkTreatment.waived
      ? ` plus a one-time ${catchUpBkLabel} fee for catch up bookkeeping`
      : ''

  return `${freeTrialDays} days free then ${productPriceLabel} billed ${
    isAnnual ? 'annually' : 'monthly'
  }${tail}`
}

/**
 * How the Catch Up Bookkeeping one-time fee is handled for this signup.
 *
 * - `charge-at-signup` — a line item on the signup checkout, paid now. Two-checkout
 *   users also land here; they pay it in a separate checkout right after signup.
 * - `defer-to-post-trial` — omitted from checkout and collected after the trial ends
 *   via the paywall (`CatchUpBookkeepingUnpaid`), gated on `paymentRequired`.
 * - `waived` — never charged, at signup or after. The API writes
 *   `paymentRequired: false` for these memberships, which is what actually suppresses
 *   the paywall; a discount alone would not, since a deferred trial has no line item
 *   to discount.
 *
 * `serverWaived` covers waivers the client cannot derive, where eligibility depends on
 * data only the API holds. It is checked before the trial branches because such a
 * cohort need not be on a trial at all.
 *
 * Every surface that states or bills the fee reads this, so the copy shown at signup
 * cannot disagree with what is charged.
 */
export const catchUpBkTreatment = ({
  trialCode,
  freeTrialEnabled,
  useTwoCheckoutFlow,
  waiverEnabled,
  serverWaived = false,
}: {
  trialCode: string | null | undefined
  freeTrialEnabled: boolean
  useTwoCheckoutFlow: boolean
  waiverEnabled: boolean
  serverWaived?: boolean
}): CatchUpBkTreatment => {
  if (serverWaived) {
    return CatchUpBkTreatment.waived
  }

  if (!freeTrialEnabled) {
    return CatchUpBkTreatment.chargeAtSignup
  }

  if (isCatchUpBkWaived({ trialCode, freeTrialEnabled, waiverEnabled })) {
    return CatchUpBkTreatment.waived
  }

  return useTwoCheckoutFlow
    ? CatchUpBkTreatment.chargeAtSignup
    : CatchUpBkTreatment.deferToPostTrial
}
